/**
 * Bajar una imagen a la cache del browser SIN mostrarla todavía.
 *
 * Existe para la vista de detalle de una creación: la grilla muestra el `opt/` de
 * 800px (~70 KB) y el detalle quiere el `full/`, que es el archivo tal como lo
 * devolvió el proveedor — en 4K son 8 MB (ADR-2026-09-14-el-detalle-baja-el-original-de-8-mb).
 * Cambiarle el `src` al `<img>` apenas se firma la URL deja al usuario esperando esos
 * 8 MB sin nada que mirar; con esta precarga el `src` se cambia recién cuando la HD ya
 * está, así que el salto es instantáneo y mientras tanto se sigue viendo el `opt`.
 *
 * NO le pone `crossOrigin` ni `referrerPolicy` a propósito: el `<img>` que después
 * muestra esta misma URL tampoco los tiene, y si no coinciden el browser la baja DOS
 * veces (la cache está particionada por esos atributos, ADR-083). Lo mismo vale para
 * cualquier caller nuevo: tiene que precargar con los mismos atributos con los que va a
 * mostrar.
 */

/** Lo único que la precarga usa de `new Image()`. Inyectable para poder testearla. */
export type ImagenPrecargable = {
  onload: (() => void) | null;
  onerror: (() => void) | null;
  src: string;
};

/**
 * Resuelve `true` si la imagen terminó de bajar y `false` si falló o si la url venía
 * vacía. NUNCA rechaza: el caller la usa para decidir si cambia lo que muestra, y un
 * error acá sólo significa "seguí mostrando lo que tenías".
 */
export function precargarImagen(
  url: string,
  // `HTMLImageElement` cumple este contrato, pero los tipos del DOM declaran los
  // handlers recibiendo el `Event` que acá no se mira: el cast es sólo para eso.
  crearImagen: () => ImagenPrecargable = () =>
    new Image() as unknown as ImagenPrecargable,
): Promise<boolean> {
  if (!url) return Promise.resolve(false);
  return new Promise<boolean>((resolve) => {
    const img = crearImagen();
    // Los handlers ANTES del src: si el archivo ya está en la cache del browser, el
    // evento puede salir en cuanto se asigna y un handler puesto después lo pierde.
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}
